/**
 * A bound on any promise, so a call that never settles surfaces as an error instead of a
 * spinner that runs forever.
 *
 * ⚠️ WHY THIS IS NOT AN `AbortSignal` ON `fetch`. The obvious place to bound a Supabase
 * call is the transport, and for this failure it is the wrong place. `supabase.rpc()`
 * resolves the session before it builds the request, so when the auth Web Lock is stuck
 * the hang happens BEFORE `fetch` is ever called. There is no request to abort. Measured
 * 2026-08-18: a `create_sub_account` call sat on its spinner for 30 minutes with no row of
 * any kind in the API logs, not even a CORS preflight.
 *
 * So this bounds the promise the caller is actually awaiting, whatever it is waiting on
 * underneath: the lock, DNS, a dead socket, or a response that never arrives.
 *
 * ⚠️ WHAT IT DOES NOT DO. `Promise.race` does not cancel the losing promise. If the
 * underlying call is genuinely wedged it stays pending for the life of the page; what
 * changes is that the CALLER stops waiting on it and can tell the user something true.
 * That is the whole intent: a bound here is a reporting guarantee, not a cancellation one.
 * The fix for a wedged call is to stop wedging it. See the header on the
 * `onAuthStateChange` handler in `contexts/AuthContext.tsx` for the one that caused this.
 *
 * ⚠️ IT MUST NOT BECOME A RETRY. A timeout on a MUTATION means "no answer", never "it did
 * not happen". `create_sub_account` may have committed and had its reply lost, so the copy
 * shown to the user says the state is unknown and to reload before retrying, rather than
 * inviting a second attempt that could create a duplicate.
 */

/** Thrown when the operation outlives its budget. */
export class OperationTimeoutError extends Error {
  /**
   * A branded flag rather than an `instanceof` check, because this error crosses module
   * and bundle boundaries where `instanceof` stops being reliable. Callers test the flag.
   */
  readonly isTimeout = true;

  readonly label: string;
  readonly timeoutMs: number;

  constructor(label: string, timeoutMs: number) {
    super(`${label} did not respond within ${timeoutMs}ms.`);
    this.name = 'OperationTimeoutError';
    this.label = label;
    this.timeoutMs = timeoutMs;
  }
}

/** True for an error produced by `withTimeout`, across bundle boundaries. */
export function isOperationTimeout(error: unknown): error is OperationTimeoutError {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { isTimeout?: unknown }).isTimeout === true
  );
}

export interface WithTimeoutOptions {
  /** How long to wait before giving up. */
  timeoutMs: number;
  /** Names the operation in the error message, so a caught timeout says what stalled. */
  label: string;
  /** Called when the budget is exceeded. For logging; it must not throw. */
  onTimeout?: (label: string, timeoutMs: number) => void;
}

/**
 * Resolve `operation`, or reject with `OperationTimeoutError` once `timeoutMs` elapses.
 *
 * The timer is always cleared, including on the success and rejection paths, so a resolved
 * call leaves nothing pending behind it.
 *
 * ⚠️ `PromiseLike`, NOT `Promise`, and that is load-bearing rather than generosity.
 * `supabase.rpc()` and every PostgREST query return a `PostgrestFilterBuilder`, which is a
 * thenable that only issues its request when something calls `.then()`. It has no `catch`
 * or `finally`, so typing this as `Promise` rejects the exact call sites it exists for.
 * `Promise.resolve` adopts a thenable and subscribes to it immediately, which is both what
 * the type needs and what actually sends the request.
 */
export function withTimeout<T>(
  operation: PromiseLike<T>,
  { timeoutMs, label, onTimeout }: WithTimeoutOptions
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;

  const bound = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => {
      try {
        onTimeout?.(label, timeoutMs);
      } catch {
        // A logging callback must never replace the timeout with its own failure.
      }
      reject(new OperationTimeoutError(label, timeoutMs));
    }, timeoutMs);
  });

  return Promise.race([Promise.resolve(operation), bound]).finally(() => {
    if (timer !== undefined) clearTimeout(timer);
  }) as Promise<T>;
}
